import { useCallback } from 'react';
import { useUIStore } from '../store';

/**
 * Custom hook for showing confirmation dialogs
 */
const useConfirm = () => {
  const { openConfirmDialog, closeConfirmDialog } = useUIStore();

  /**
   * Show a confirmation dialog
   * @param {Object} options - Dialog options
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false otherwise
   */
  const confirm = useCallback(
    (options = {}) => {
      const {
        title = 'Confirm Action',
        message = 'Are you sure you want to proceed?',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        confirmColor = 'primary',
        severity = 'warning', // 'info' | 'warning' | 'error' | 'success'
      } = options;

      return new Promise((resolve) => {
        openConfirmDialog({
          title,
          message,
          confirmText,
          cancelText,
          confirmColor,
          severity,
          onConfirm: () => {
            closeConfirmDialog();
            resolve(true);
          },
          onCancel: () => {
            closeConfirmDialog();
            resolve(false);
          },
        });
      });
    },
    [openConfirmDialog, closeConfirmDialog]
  );

  /**
   * Show a delete confirmation dialog
   * @param {string} itemName - Name of the item being deleted
   * @returns {Promise<boolean>}
   */
  const confirmDelete = useCallback(
    (itemName = 'this item') => {
      return confirm({
        title: 'Delete Confirmation',
        message: `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmColor: 'error',
        severity: 'error',
      });
    },
    [confirm]
  );

  /**
   * Show a discard changes confirmation dialog
   * @returns {Promise<boolean>}
   */
  const confirmDiscard = useCallback(() => {
    return confirm({
      title: 'Discard Changes',
      message: 'You have unsaved changes. Are you sure you want to discard them?',
      confirmText: 'Discard',
      cancelText: 'Keep Editing',
      confirmColor: 'warning',
      severity: 'warning',
    });
  }, [confirm]);

  /**
   * Show a logout confirmation dialog
   * @returns {Promise<boolean>}
   */
  const confirmLogout = useCallback(() => {
    return confirm({
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      confirmText: 'Logout',
      cancelText: 'Cancel',
      confirmColor: 'primary',
      severity: 'info',
    });
  }, [confirm]);

  /**
   * Show a save confirmation dialog
   * @param {string} itemName - Name of the item being saved
   * @returns {Promise<boolean>}
   */
  const confirmSave = useCallback(
    (itemName = 'changes') => {
      return confirm({
        title: 'Save Changes',
        message: `Are you sure you want to save ${itemName}?`,
        confirmText: 'Save',
        cancelText: 'Cancel',
        confirmColor: 'success',
        severity: 'info',
      });
    },
    [confirm]
  );

  /**
   * Show a publish confirmation dialog
   * @param {string} itemName - Name of the item being published
   * @returns {Promise<boolean>}
   */
  const confirmPublish = useCallback(
    (itemName = 'this') => {
      return confirm({
        title: 'Publish Confirmation',
        message: `Are you sure you want to publish ${itemName}? This will make it live.`,
        confirmText: 'Publish',
        cancelText: 'Cancel',
        confirmColor: 'success',
        severity: 'warning',
      });
    },
    [confirm]
  );

  return {
    confirm,
    confirmDelete,
    confirmDiscard,
    confirmLogout,
    confirmSave,
    confirmPublish,
  };
};

export default useConfirm;
